<template>

    <div
        :class="{
            'fixed top-0 right-0 bottom-0 left-0 h-full fullscreen': fullscreen,
            'rounded-lg': !fullscreen,
        }"
        class="flex flex-col bg-white canvas::editor-Admin">

        <div v-if="importSuccess" class="p-4 border border-green-200 rounded-md bg-green-50">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="w-5 h-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                         fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <div class="text-sm text-green-700">
                        <p>
                            {{ importSuccessMessage }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div
            :class="{
                'opacity-50 pointer-events-none': importingCanvasBlocks,
            }"
        >

            <ToolbarTop
                :activeCanvasVersion="activeCanvasVersion"
                :canvasVersion.sync="canvasVersion"
                :versionState="versionState"
                :activeMode="activeMode"
                :title="title"
                :instruction="instruction"
                :currentLanguageToShow="isMultiLanguage ? currentLanguage : null"
                :fullscreen.sync="fullscreen"
                :hasMultipleConceptCanvases="hasMultipleConceptCanvases"
            />

            <div class="relative flex flex-col w-full h-full">

                <div class="flex canvasWrapper">

                    <canvas-component
                        ref="canvasComponent"
                        :primaryLanguage="primaryLanguage"
                        :language="language"
                        :canvasVersion.sync="canvasVersion"
                        :activeMode="activeMode"
                        :activeViewport="activeViewport"
                        :stateSaving="stateSaving"
                        :galleryDraggingBlock="galleryDraggingBlock"
                        :canManageBlocks="canManageBlocks"
                        @loaded="iframeLoaded = true"
                        @importCompleted="handleImportCompleted"
                        @importing="importingCanvasBlocks = $event"
                    />

                    <gallery
                        v-if="activeMode == 'overview' && canManageBlocks"
                        :canvasBlocksGalleryData="localGalleryData"
                        :canvasVersion.sync="canvasVersion"
                        :canvasId="id"
                        :isSuperadmin="isSuperadmin"
                        @onDraggingBlock="galleryDraggingBlock = $event"
                        @addBlock="$refs['canvasComponent'].pageAddBlock($event)"
                        @showBlockContextMenu="openBlockContextMenu($event.block, $event.x, $event.y)"
                    />

                </div>

                <BlockContextMenu
                    :visible="contextMenu.visible"
                    :x="contextMenu.x"
                    :y="contextMenu.y"
                    :block="contextMenu.block || {}"
                    :context-key="contextKey"
                    :context-label="contextLabel"
                    :already-excluded="contextMenu.alreadyExcluded"
                    :reseller-enabled="resellerEnabled"
                    @exclude-context="beginExclusion('context')"
                    @disable-website="beginExclusion('website')"
                    @disable-reseller="beginExclusion('reseller')"
                    @close="closeBlockContextMenu"
                />

                <BlockUsageWarningModal
                    :visible="warning.visible"
                    :loading="warning.loading"
                    :title="warning.title"
                    :block-name="warning.blockName"
                    :count="warning.count"
                    :sample="warning.sample"
                    :scope-description="warning.scopeDescription"
                    :confirm-label="warning.confirmLabel"
                    @confirm="confirmExclusion"
                    @cancel="cancelExclusion"
                />

                <div class="sticky bottom-0 z-10 py-3 my-0 text-right bg-white toolbarBottom" :class="{ loading: !iframeLoaded || stateSaving }">
                    <div class="flex flex-wrap gap-4 py-0">

                        <ModeAndViewportToolbar
                            :activeMode.sync="activeMode"
                            :activeViewport.sync="activeViewport"
                            :canManageBlocks="canManageBlocks"
                        />

                        <VersionActions
                            :canvasVersion.sync="canvasVersion"
                            :version-state="versionState"
                            :useVersioning="useVersioning"
                            :currentLanguage="currentLanguage"
                            @update.canvasVersion="setNewLanguageCanvasVersion($event)"
                            @onNewCanvasVersions="setNewCanvasVersions($event)"
                            @canvasVersionBackToLive="canvasVersionBackToLive"
                        />

                    </div>
                </div>

            </div>

        </div>

    </div>

</template>

<script>

import Canvas from "./admin/Canvas";
import Gallery from "./admin/Gallery";
import VersionActions from "./admin/VersionActions";
import ModeAndViewportToolbar from "./admin/ModeAndViewportToolbar";
import ToolbarTop from "./admin/ToolbarTop";
import BlockContextMenu from "./admin/BlockContextMenu";
import BlockUsageWarningModal from "./admin/BlockUsageWarningModal";
import Swal from 'sweetalert2';

export default {

    provide: function ()
    {
        return {
            editorComponent: this,
        };
    },

    components: {
        ModeAndViewportToolbar,
        'canvas-component': Canvas,
        Gallery,
        ToolbarTop,
        VersionActions,
        BlockContextMenu,
        BlockUsageWarningModal,
    },

    props: {
        id: String,
        title: String,
        instruction: String,

        primaryLanguage: String,
        language: String,
        canvasVersions: Object,
        canvasBlocksGalleryData: Array,
        hasMultipleConceptCanvases: Object,

        useVersioning: Boolean,
        canManageBlocks: { type: Boolean, default: true },

        // Canvas block exclusion context.
        contextKey: { type: String, default: null },
        contextLabel: { type: String, default: null },
        isSuperadmin: { type: Boolean, default: false },
        resellerEnabled: { type: Boolean, default: false },
    },

    data()
    {
        return {
            activeMode: null,
            activeViewport: 'xl',
            showCanvasVersion: null, // Used to show the canvas version in the iframe.
            canvasVersionsLocal: this.canvasVersions,
            localVersionState: null,
            iframeLoaded: false,
            stateSaving: false, // Manipulated else where.
            galleryDraggingBlock: null,
            currentLanguage: this.language || Object.keys(this.canvasVersions)[0],
            fullscreen: false,
            importSuccess: false,
            importSuccessMessage: '',
            importingCanvasBlocks: false,

            // Local copy of the gallery data — mutated client-side when an
            // exclusion rule is created so the gallery updates without a
            // page reload.
            localGalleryData: JSON.parse(JSON.stringify(this.canvasBlocksGalleryData || [])),

            // Canvas block exclusion UI state (gallery-only).
            contextMenu: {
                visible: false,
                x: 0,
                y: 0,
                block: null,
                alreadyExcluded: false,
            },
            warning: {
                visible: false,
                loading: false,
                title: '',
                blockName: '',
                count: 0,
                sample: [],
                scopeDescription: '',
                confirmLabel: 'Confirm',
                pendingScope: null,
                pendingBlock: null,
            },
        };
    },

    mounted()
    {
        this.initActiveVersion();
        this.initInstance();
        this.initVersionState();
    },

    methods: {

        handleImportCompleted(canvasId) {
            // Initialize the success message immediately
            this.importSuccessMessage = "De blokken zijn succesvol geimporteerd. De pagina wordt herladen in 3 seconden...";
            this.importSuccess = true;

            // Start the countdown
            let countdown = 3;
            const interval = setInterval(() => {
                countdown--;

                // Update the message during the countdown
                this.importSuccessMessage = `De blokken zijn succesvol geimporteerd. De pagina wordt herladen in ${countdown} seconden...`;

                if (countdown === 0) {
                    clearInterval(interval);

                    window.location.reload();
                }
            }, 1000);
        },

        initActiveVersion()
        {
            // To overview due to no canvas blocks present yet? Force content mode when user cannot manage blocks.
            this.activeMode = !this.canManageBlocks || this.canvasVersion.canvas_blocks.length > 0 ? 'content' : 'overview';
        },

        initInstance()
        {
            // @ugly Set so language switcher can reach it.
            $(this.$el).data('vueInstance', this);
        },

        initVersionState()
        {
            // @ugly TODO Vue anti pattern.
            this.$watch('$refs.canvasComponent.$refs.iframe.iframeApp', () =>
            {
                // @ugly TODO Vue anti pattern.
                let iframeApp = this.$refs.canvasComponent.$refs.iframe.iframeApp;

                // Iframe app present? May be gone while iframe is loading for another language.
                if (iframeApp)
                    iframeApp.$watch('pageComponent.state.dirty', () =>
                    {
                        this.localVersionState = 'concept';
                    });
            });
        },

        setNewLanguageCanvasVersion(canvasVersion)
        {
            // Set
            this.canvasVersionsLocal[this.currentLanguage].canvasVersion = canvasVersion;
        },

        setNewCanvasVersions(canvasVersions)
        {
            // Set
            this.canvasVersionsLocal = canvasVersions;
        },

        canvasVersionBackToLive()
        {
            // Clear overwriteable version state. Computed this.versionState() will check what is right.
            this.localVersionState = null

            // TODO High: Refresh iframe.

        },

        scrollInViewport()
        {
            // Determine scrolltop.
            let scrollTop = $(this.$el).offset().top - 30;

            // Scroll
            $('html, body')
                .scrollTop(scrollTop);
        },

        // ─── Canvas block exclusion (superadmin, gallery-only) ──────────────

        openBlockContextMenu(block, x, y)
        {
            if (!this.isSuperadmin) return;

            // Clamp to viewport so the menu isn't clipped near the right/bottom edge.
            const menuWidth = 280;
            const menuHeight = 240;
            const clampedX = Math.min(x, window.innerWidth - menuWidth - 8);
            const clampedY = Math.min(y, window.innerHeight - menuHeight - 8);

            this.contextMenu = {
                visible: true,
                x: clampedX,
                y: clampedY,
                block,
                alreadyExcluded: !!(block && block.meta && block.meta.excluded),
            };
        },

        closeBlockContextMenu()
        {
            this.contextMenu.visible = false;
        },

        /**
         * Starts the exclusion flow for the currently-selected block. Fetches
         * a usage preview from the server and opens the warning modal.
         */
        beginExclusion(scope)
        {
            const block = this.contextMenu.block;
            if (!block) return;

            const contextKey = this.contextKey;
            if (scope === 'context' && !contextKey) {
                alert('No context key is set on this canvas — cannot exclude by context.');
                this.closeBlockContextMenu();
                return;
            }

            this.warning = {
                visible: true,
                loading: true,
                title: scope === 'context'
                    ? `Exclude from ${this.contextLabel || contextKey}`
                    : (scope === 'website' ? 'Hide from this website' : 'Hide from reseller'),
                blockName: (block.meta && block.meta.name) || 'Unknown block',
                count: 0,
                sample: [],
                scopeDescription: scope === 'context'
                    ? `on ${this.contextLabel || contextKey}`
                    : (scope === 'website' ? 'on this website' : 'across this reseller'),
                confirmLabel: scope === 'context' ? 'Exclude' : 'Hide',
                pendingScope: scope,
                pendingBlock: block,
            };

            this.closeBlockContextMenu();

            const payload = {
                block_key: block.id,
                scope,
                context_key: scope === 'context' ? contextKey : null,
            };

            axios.post('/admin/modules/canvas/block-exclusions/preview-usage', payload)
                .then(response => {
                    this.warning.loading = false;
                    this.warning.count = response.data.count || 0;
                    this.warning.sample = response.data.sample || [];
                    if (response.data.block_name) this.warning.blockName = response.data.block_name;
                })
                .catch(error => {
                    console.error('Failed to preview usage', error);
                    this.warning.loading = false;
                    this.warning.count = 0;
                });
        },

        confirmExclusion()
        {
            const scope = this.warning.pendingScope;
            const block = this.warning.pendingBlock;
            if (!scope || !block) return;

            const payload = {
                block_key: block.id,
                scope,
                context_key: scope === 'context' ? this.contextKey : null,
            };

            axios.post('/admin/modules/canvas/block-exclusions', payload)
                .then(response => {
                    this.applyExclusionLocally(block.id, scope);
                    this.warning.visible = false;
                })
                .catch(error => {
                    console.error('Failed to create exclusion rule', error);
                    alert(error.response?.data?.message || 'Failed to create exclusion rule.');
                    this.warning.visible = false;
                });
        },

        cancelExclusion()
        {
            this.warning.visible = false;
        },

        /**
         * Mutates localGalleryData so the gallery reflects the new rule
         * immediately, without needing a full page reload.
         */
        applyExclusionLocally(blockKey, scope)
        {
            if (scope === 'context') {
                // Mark the block as excluded in-place.
                for (const category of this.localGalleryData) {
                    for (const b of category.blocks || []) {
                        if (b.id === blockKey) {
                            this.$set(b.meta, 'excluded', true);
                            this.$set(b.meta, 'excludedReason',
                                'Not available on ' + (this.contextLabel || this.contextKey));
                        }
                    }
                }
            } else {
                // scope=website or scope=reseller — remove the block entirely.
                this.localGalleryData = this.localGalleryData.map(category => ({
                    ...category,
                    blocks: (category.blocks || []).filter(b => b.id !== blockKey),
                }));
            }
        },

    },

    computed: {

        canvasComponent()
        {
            return this.$refs['canvasComponent'];
        },

        versionState()
        {
            // Overwrite?
            if (this.localVersionState)
                return this.localVersionState;

            // Check version.
            return this.canvasVersionsLocal[this.currentLanguage].activeCanvasVersion?.id && this.canvasVersion.created_at == this.canvasVersion.updated_at ? 'live' : 'concept';
        },

        canvasVersion: {
            get: function () {
                return this.showCanvasVersion || this.canvasVersionsLocal[this.currentLanguage].canvasVersion;
            },
            set: function (value) {
                this.showCanvasVersion = value;
                return value;
            }
        },

        activeCanvasVersion()
        {
            return this.canvasVersionsLocal[this.currentLanguage].activeCanvasVersion;
        },

        isMultiLanguage()
        {
            return Object.values(this.canvasVersionsLocal).length > 1;
        },

    },

    watch: {

        'fullscreen'(newVal)
        {
            // Scroll due to fullscreen ended? We have to wait for the next redraw.
            if (!newVal)
                this.$nextTick(() => this.scrollInViewport());
        },

    }

}

</script>

<style lang="scss" scoped>

.canvas\:\:editor-Admin {
    @apply bg-white;
    z-index: 100;

    //&:not(.fullscreen) {
    //    min-height: calc(100vh - 75px);
    //}

    .toolbarBottom {
        border-top: 1px solid #ddd !important;
        transition: opacity .2s;

        &.loading {
            opacity: .5;
            pointer-evens: none;
        }
    }

    .canvasWrapper {
        min-height: 440px;
    }
}

</style>
